/** @format */

import { useEffect, useMemo, useRef, useState } from 'react';
import { makeStyles } from '@mui/styles';
import LoadingScreen from 'src/components/LoadingScreen';
import { prependApiUrlToCmsPaths } from 'src/utils/cmsContent';
import {
  cleanupCmsEmbedProviders,
  loadCmsHtmlScripts,
  parseHtmlScripts,
  providerWatchesReady,
  resolveProvidersForContent,
  setupMarketingFormPhoneValidation,
  subscribeProvidersReady
} from 'src/utils/cmsEmbed';

const EMBED_LOAD_TIMEOUT_MS = 20000;

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative'
  },
  rootLoading: {
    minHeight: 280
  },
  loader: {
    position: 'absolute',
    inset: 0,
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.background.default
  },
  contentHidden: {
    visibility: 'hidden'
  }
}));

type CmsHtmlContentProps = {
  html?: string;
  className?: string;
};

export function CmsHtmlContent({ html, className }: CmsHtmlContentProps) {
  const classes = useStyles();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const normalizedHtml = useMemo(() => prependApiUrlToCmsPaths(html), [html]);
  const { htmlWithoutScripts, scriptSources } = useMemo(
    () => parseHtmlScripts(normalizedHtml),
    [normalizedHtml]
  );
  const scriptSourcesKey = scriptSources.join('\0');
  const activeProviders = useMemo(
    () => resolveProvidersForContent({ html: normalizedHtml, scriptSources }),
    [normalizedHtml, scriptSourcesKey]
  );
  const providersKey = activeProviders.map(provider => provider.id).join(',');

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const hasScripts = scriptSources.length > 0;
    const watchesReady = providerWatchesReady(activeProviders);
    let cancelled = false;

    const clearLoading = () => {
      if (!cancelled) setIsLoading(false);
    };

    if (hasScripts) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }

    const fragment = document.createRange().createContextualFragment(htmlWithoutScripts || '');
    container.innerHTML = '';
    container.appendChild(fragment);

    const unsubscribeReady = subscribeProvidersReady(activeProviders, {
      container,
      clearLoading
    });

    const hasMarketingProvider = activeProviders.some(provider => provider.id === 'dynamics365-marketing');
    const phoneValidationController = new AbortController();
    const bindMarketingPhoneValidation = () => {
      if (!hasMarketingProvider || cancelled) return;
      setupMarketingFormPhoneValidation(container, phoneValidationController.signal);
    };
    const onMarketingFormLoad = (event: Event) => {
      if (!container.contains(event.target as Node)) return;
      bindMarketingPhoneValidation();
    };

    if (hasMarketingProvider) {
      document.addEventListener('d365mkt-afterformload', onMarketingFormLoad, {
        signal: phoneValidationController.signal
      });
    }

    const safetyTimeout = hasScripts ? window.setTimeout(clearLoading, EMBED_LOAD_TIMEOUT_MS) : undefined;

    const mountScripts = async () => {
      cleanupCmsEmbedProviders(activeProviders);
      if (!hasScripts || cancelled) {
        clearLoading();
        return;
      }

      try {
        await loadCmsHtmlScripts(scriptSources, activeProviders);
        if (cancelled) return;
        bindMarketingPhoneValidation();
        if (!watchesReady) {
          clearLoading();
        }
      } catch (error) {
        console.error('Failed to load CMS HTML scripts', error);
        clearLoading();
      }
    };

    mountScripts();

    return () => {
      cancelled = true;
      phoneValidationController.abort();
      if (safetyTimeout) window.clearTimeout(safetyTimeout);
      unsubscribeReady.forEach(unsubscribe => unsubscribe());
      cleanupCmsEmbedProviders(activeProviders);
      setIsLoading(false);
    };
  }, [htmlWithoutScripts, scriptSourcesKey, providersKey, scriptSources, activeProviders]);

  const rootClassName = [classes.root, isLoading && classes.rootLoading, className].filter(Boolean).join(' ');

  return (
    <div className={rootClassName}>
      {isLoading && (
        <div className={classes.loader} aria-live="polite" aria-busy="true">
          <LoadingScreen />
        </div>
      )}
      <div
        ref={containerRef}
        className={isLoading ? classes.contentHidden : undefined}
        aria-hidden={isLoading || undefined}
      />
    </div>
  );
}
